/**
 * Get proxied image URL to bypass CORS issues
 * @param {string} originalUrl - Original image URL
 * @returns {string} - Proxied image URL
 */
export const getProxiedImageUrl = (originalUrl) => {
  if (!originalUrl) return null;
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  // If image is already from our domain or is a data URL, return as is
  if (originalUrl.startsWith('data:') || originalUrl.includes(window.location.hostname)) {
    return originalUrl;
  }
  
  // Use backend proxy for external images
  return `${BACKEND_URL}/api/proxy-image?url=${encodeURIComponent(originalUrl)}`;
};

/**
 * Format price in Euro
 */
export const formatPrice = (price) => {
  return `€${parseFloat(price).toFixed(2)}`;
};

/**
 * Truncate text to specified length
 */
export const truncateText = (text, maxLength = 150) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};


/**
 * Get product URL path using slug (SEO-friendly) or fallback to SKU
 */
export const getProductPath = (product) => {
  if (!product) return '/products';
  const path = product.slug || product.sku;
  return `/products/${path}`;
};


// -------- Search: synonyms & Italian stemmer helpers --------
// Italian singular/plural conversions are noisy; we use a light stemmer that
// strips common trailing vowels/plurals so "pastiglia" / "pastiglie" both
// reduce to "pastigl". User queries are stemmed the SAME way at query time,
// so plural/singular differences vanish naturally.
const _italianStem = (w) => {
  if (!w) return '';
  let s = String(w).toLowerCase();
  // strip common plural/feminine endings only when the word is long enough
  if (s.length > 4) {
    s = s.replace(/(izzatori|izzatore|azioni|azione|amento|amenti|isti|iste|ista|hetti|hetto|hetta|ini|ino|ina|ine|oni|one|ori|ore|iche|ico|ica|are|ere|ire|ari|eri|iri|ati|ato|ate|uto|uti|ute|ent[eiao]|acc[eio])$/,'');
  }
  if (s.length > 3) {
    s = s.replace(/(ie|ia|ii|io|oo|ee|aa)$/,'');
    s = s.replace(/[aeiou]$/,'');
    // Italian plural h-insertion: "disco→dischi" both stem to "disc"
    s = s.replace(/h$/,'');
  }
  return s;
};

// Query-time synonym expansion. Every entry maps a canonical stem to the
// alternative words a customer might type. The stemmer above makes most
// singular/plural cases work without needing entries here.
// Keys should be short stems that survive the italian stemmer.
const _SEARCH_SYNONYMS = {
  pastigl: ['pastigl', 'pattin'],   // pastiglie ↔ pattini
  pattin: ['pattin', 'pastigl'],
  disc: ['disc'],                    // dischi/disco/disch  → all reduce to substring "disc"
  fren: ['fren', 'frenat'],          // freno/freni/frenata
  adesiv: ['adesiv', 'sticker', 'decal'],
  sticker: ['sticker', 'adesiv', 'decal'],
  decal: ['decal', 'adesiv', 'sticker'],
  parafang: ['parafang', 'fender'],
  filtr: ['filtr', 'filter'],
  guarniz: ['guarniz', 'gasket'],
  paraoli: ['paraoli', 'oil seal'],
  scaric: ['scaric', 'silenziat', 'muffler', 'exhaust'],
  silenziat: ['silenziat', 'scaric', 'muffler'],
  manopol: ['manopol', 'grip'],
  copertur: ['copertur', 'cover'],
  paracaten: ['paracaten', 'chain guard'],
  coprisell: ['coprisell', 'copertina sella'],
  plastic: ['plastic', 'plastik'],
  paramain: ['paramain', 'handguard'],
};

// Build the list of variants for a single query token
const _tokenVariants = (raw) => {
  const t = String(raw).toLowerCase();
  const stem = _italianStem(t);
  const set = new Set([t]);
  if (stem && stem.length >= 2) set.add(stem);
  // Look up synonyms by stem
  for (const key of Object.keys(_SEARCH_SYNONYMS)) {
    if (stem.startsWith(key) || t.startsWith(key)) {
      _SEARCH_SYNONYMS[key].forEach((v) => set.add(v));
    }
  }
  return Array.from(set);
};

/**
 * Token-based AND search with light stemming + synonyms.
 * Returns true if EVERY token in `query` matches at least one product field.
 * A token matches when one of its variants (stem, synonym) is a substring
 * of the aggregated haystack (case-insensitive).
 *
 * Searched fields: sku, name_it, name_en, description_it, brand, category,
 * compatible_models (all entries joined).
 */
export const matchesProductSearch = (product, query) => {
  if (!query || !product) return true;
  const tokens = String(query).toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const compat = Array.isArray(product.compatible_models)
    ? product.compatible_models.join(' ')
    : '';
  const haystack = [
    product.sku,
    product.name_it,
    product.name_en,
    product.description_it,
    product.brand,
    product.category,
    compat,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return tokens.every((t) => _tokenVariants(t).some((v) => haystack.includes(v)));
};

/**
 * Return a numeric relevance score for `product` against `query`. Higher =
 * better match. Products whose TITLE contains all tokens score highest;
 * matches that live only in compatible_models rank lower so the search bar
 * shows the truly-matching products first.
 *   +10 per token found in name_it/name_en
 *   +6  per token found in brand/sku
 *   +3  per token found in category
 *   +2  per token found in description_it
 *   +1  per token found only in compatible_models
 */
export const scoreProductSearch = (product, query) => {
  if (!query || !product) return 0;
  const tokens = String(query).toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return 0;
  const nameHay = `${product.name_it || ''} ${product.name_en || ''}`.toLowerCase();
  const brandHay = `${product.brand || ''} ${product.sku || ''}`.toLowerCase();
  const catHay = (product.category || '').toLowerCase();
  const descHay = (product.description_it || '').toLowerCase();
  const compatHay = (Array.isArray(product.compatible_models) ? product.compatible_models.join(' ') : '').toLowerCase();
  let score = 0;
  for (const t of tokens) {
    const variants = _tokenVariants(t);
    if (variants.some((v) => nameHay.includes(v))) { score += 10; continue; }
    if (variants.some((v) => brandHay.includes(v))) { score += 6; continue; }
    if (variants.some((v) => catHay.includes(v))) { score += 3; continue; }
    if (variants.some((v) => descHay.includes(v))) { score += 2; continue; }
    if (variants.some((v) => compatHay.includes(v))) { score += 1; continue; }
  }
  return score;
};
